import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach Bearer token from auth store
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Lazy import to avoid circular dependency with the store
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { state?: { token?: string } };
        const token = parsed?.state?.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Ignore parse errors
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (reason: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
}

// Endpoints that return 401 for bad credentials rather than an expired session —
// they must never trigger the refresh-token flow or the redirect-to-login below.
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/change-password',
  // Covers /forgot-password, /reset-password and /reset-password/validate —
  // all pre-login, so a failure is never a stale session to refresh.
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/password-reset/status',
  // The SSO handoff exchange 401s on an expired or already-used code, which
  // is a dead end, not a stale session — letting it reach the refresh flow
  // below would produce a redirect loop on the callback page.
  '/auth/oauth/exchange',
  '/auth/providers',
];

// Response interceptor — refresh token on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isAuthEndpoint = AUTH_ENDPOINTS.some((endpoint) => originalRequest.url?.includes(endpoint));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const raw = localStorage.getItem('auth-storage');
        const parsed = raw ? (JSON.parse(raw) as { state?: { token?: string; refreshToken?: string } }) : null;
        const refreshToken = parsed?.state?.refreshToken;

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
          `${BASE_URL}/auth/refresh`,
          { refreshToken },
        );

        // Update stored tokens
        if (parsed?.state) {
          parsed.state.token = data.access_token;
          parsed.state.refreshToken = data.refresh_token;
          localStorage.setItem('auth-storage', JSON.stringify(parsed));
        }

        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Clear auth state on refresh failure
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
